import { TrashIcon } from "@xola/icons";
import { Badge } from "@xola/ui-kit";
import { DateFormat, formatDate } from "common";
import React, { cloneElement, useState } from "react";
import { useHistory } from "react-router-dom";
import { Cron, Permission, Report, ReportObject, User } from "types";
import { schedulerApi } from "../../../api/schedulerApi";
import { useSeller, useUser } from "../../../hooks/useAccount";
import { useAuthorization } from "../../../hooks/useAuthorization";
import { useInvalidate } from "../../../hooks/useInvalidation";
import { useResource } from "../../../hooks/useResource";
import { NoTranslate } from "../../Common/NoTranslate";
import { PromptModal } from "../../Common/PromptModal";
import { SecondaryButton } from "../../Common/SecondaryButton";
import { SuggestedReport } from "./SuggestedReports";

interface ReportTileProps {
    report: Report;
    isDeleting: boolean;
    onDelete: (reportId?: ID) => Promise<void>;
    suggestedReports: SuggestedReport[];
}

export const ReportTile = ({ report, onDelete, isDeleting, suggestedReports }: ReportTileProps) => {
    const user = useUser();
    const seller = useSeller();
    const history = useHistory();
    const invalidate = useInvalidate();
    const { isGranted } = useAuthorization();
    const [isDeletePromptOpen, setIsDeletePromptOpen] = useState(false);

    const isSavedReport = !!report.id;
    const suggestedReport = suggestedReports.find(({ report: { object } }) => object === report.object);

    const description = isSavedReport ? (
        <>
            Customized <var data-var="reportTitle">{suggestedReport?.report.title ?? report.title}</var> report
        </>
    ) : (
        suggestedReport?.description
    );

    const handleReportDelete = async () => {
        const { data: crons } = await schedulerApi.get<Cron[]>("/crons", {
            params: { seller: seller.id, user: user.id, remoteId: report.id },
        });

        if (crons?.length) {
            await Promise.allSettled(crons.map(async (cronJob) => schedulerApi.delete(`/crons/${cronJob.id}`)));
            invalidate("crons");
        }

        await onDelete(report.id);
    };

    const handleClick = () => {
        const suffix = report.id ?? `new/?object=${report.object}`; // Should be /? instead of ? to work around Amplify
        history.push(`/reports/customReports/${suffix}`);
    };

    if (suggestedReport?.report.object === ReportObject.TICKET_REDEMPTION) {
        suggestedReport.hidden = !isGranted(Permission.REPORTS_TICKET_REDEMPTION_VIEW);
    }

    if (!suggestedReport || suggestedReport.hidden) {
        return null;
    }

    return (
        <div
            className="group flex space-x-2 rounded border border-gray-lighter p-6 hover:cursor-pointer hover:border-blue hover:bg-blue-lighter"
            onClick={handleClick}
        >
            <div className="pt-0.5">{cloneElement(suggestedReport.icon, { size: "medium" })}</div>
            <div className="w-full space-y-2.5">
                <h3 className="flex h-6 justify-between" data-testid="reportTitleLocator">
                    <NoTranslate if={isSavedReport} className="line-clamp-1">
                        {report.title}
                    </NoTranslate>

                    {suggestedReport.beta && (
                        <Badge color="warning" size="small" className="!rounded">
                            Beta
                        </Badge>
                    )}

                    {isSavedReport && (
                        <SecondaryButton
                            size="small"
                            className="hidden group-hover:inline"
                            onClick={(e: any) => {
                                e.stopPropagation();
                                setIsDeletePromptOpen(true);
                            }}
                        >
                            <TrashIcon />
                        </SecondaryButton>
                    )}
                </h3>

                <div className="text-base leading-p1 tracking-tightest text-gray-darker line-clamp-3">
                    {description}
                </div>

                {report.createdAt && <ReportCreator report={report} />}
            </div>
            <PromptModal
                title="Delete Report"
                isLoading={isDeleting}
                isOpen={isDeletePromptOpen}
                onClose={(e: any) => {
                    setIsDeletePromptOpen(false);
                    e.stopPropagation();
                }}
                onConfirm={(e: any) => {
                    handleReportDelete();
                    e.stopPropagation();
                }}
            >
                Are you sure you want to delete the <var data-var="reportTitle">{report.title}</var> report?
            </PromptModal>
        </div>
    );
};

const ReportCreator = ({ report }: { report: Report }) => {
    const { data: user } = useResource<User>(["users", report.createdBy]);
    if (!report.createdAt) {
        return null;
    }

    return (
        <div className="text-sm leading-p1 tracking-tightest text-gray-dark">
            Created: <var data-var="createdAt">{formatDate(report.createdAt, DateFormat.DATE)}</var>{" "}
            {user?.name && (
                <>
                    by <var data-var="user">{user.name}</var>
                </>
            )}
        </div>
    );
};
